import React, { useContext, useLayoutEffect, useRef, useState } from 'react';
import {
  KEY_NAV_DIR,
  Modal,
  ModalSection,
  PositionAwareMenu,
  SIDEBAR_BREAKPOINTS,
  Transition,
  useResponsiveValue,
  fontStyles as fs,
  Icon,
} from '@monash/portal-react';
import c from '../userInitials/userInitials.module.scss';
import UserInitialSection from './UserInitialSection';
import { useOnOutsideClick } from '../../hooks/use-on-outside-click';
import UserInitialsItem from './UserInitialsItem';
import portalLink from '../../utils/portalLinks';
import { AuthContext } from '../../auth/AuthContext';

const MENU_HEIGHT_DEFAULT = 640;

const UserInitialsContent = ({
  triggerRef,
  shown,
  setShown,
  title = 'User menu',
  align = 'right',
  avatarSrc,
  avatarAlt,
  name,
  email,
  id,
  pronouns,
  children,
}) => {
  const { logout } = useContext(AuthContext);

  const [mounted, setMounted] = useState(shown);
  const [verticalClass, setVerticalClass] = useState(c.down);
  const [selected, setSelected] = useState(-1);

  const containerRef = useRef(null);
  const menuWrapperRef = useRef(null);
  const menuItemsRef = useRef([]);

  const size = useResponsiveValue(SIDEBAR_BREAKPOINTS);
  const isMobileView = ['XS', 'S'].includes(size);

  const collapseMenu = (focusTrigger = false) => {
    setSelected(-1);
    setShown(false);
    if (focusTrigger) {
      triggerRef.current?.focus();
    }
  };

  useOnOutsideClick({
    refs: [triggerRef, containerRef],
    fn: collapseMenu,
    selectors: '[data-ignore-outside-click]',
  });

  const onComponentShown = () => {
    setMounted(true);
    if (shown && selected <= -1) {
      // Note: need to wait for Transition and PositionAwareMenu render 'menuWrapperRef' in DOM
      setTimeout(() => {
        menuWrapperRef.current?.focus();
      }, 0);
    }
  };

  const onReposition = (left, down) => {
    setVerticalClass(down ? c.down : c.up);
  };

  // Get max height for use menu
  const [menuHeight, setMenuHeight] = useState(MENU_HEIGHT_DEFAULT);
  const bottomMarginOffset = 60;
  useLayoutEffect(() => {
    function updateHeight() {
      const menuStartHeight = triggerRef.current?.getBoundingClientRect().y;
      const height = window.innerHeight - menuStartHeight - bottomMarginOffset;
      setMenuHeight(height);
    }
    window.addEventListener('resize', updateHeight);
    updateHeight();
    return () => window.removeEventListener('resize', updateHeight);
  }, []);

  const handleOnMenuKeyDown = (e) => {
    // only applies when menu is expanded
    if (!shown) return;

    // collapse menu on ESC
    if (e.key === 'Escape') {
      collapseMenu(true);
      return;
    }

    // key nav direction
    let keyNavDir;
    if (
      e.key === 'ArrowUp' ||
      e.key === 'ArrowLeft' ||
      (e.key === 'Tab' && e.shiftKey)
    ) {
      keyNavDir = KEY_NAV_DIR.PREV;
    } else if (
      e.key === 'ArrowDown' ||
      e.key === 'ArrowRight' ||
      (e.key === 'Tab' && !e.shiftKey)
    ) {
      keyNavDir = KEY_NAV_DIR.NEXT;
    }

    // find key nav target and focus
    if (keyNavDir === KEY_NAV_DIR.PREV) {
      const targetIndex =
        selected <= 0 ? menuItemsRef.current.length - 1 : selected - 1;
      setSelected(targetIndex);
      e.preventDefault();
      menuItemsRef.current[targetIndex]?.focus();
    } else if (keyNavDir === KEY_NAV_DIR.NEXT) {
      const targetIndex =
        selected >= menuItemsRef.current.length - 1 ? 0 : selected + 1;
      setSelected(targetIndex);
      e.preventDefault();
      menuItemsRef.current[targetIndex]?.focus();
    }
  };

  const baseSection = (
    <UserInitialSection key="base">
      <UserInitialsItem
        id="userGuide"
        href={portalLink('user-guide')}
        text="User guide"
        icon={<Icon.Help />}
      />
      <UserInitialsItem
        id="feedback"
        href="/feedback"
        text="Feedback & Bugs"
        icon={<Icon.Activity />}
      />
      <UserInitialsItem
        id="termOfUse"
        href="https://www.monash.edu/policy-bank/policies-and-procedures/management/governance/Privacy.pdf"
        text="Terms of Use"
        icon={<Icon.Note />}
      />
      <UserInitialsItem
        id="logOut"
        onClick={() => logout('https://monash.edu')}
        text="Log out"
        icon={<Icon.Logout />}
      />
    </UserInitialSection>
  );

  const version = process.env.REACT_APP_VERSION || '1.0.0';

  const versionSection = (
    <UserInitialSection key="version">
      <span className={`${fs.label} ${c.version}`}>Version {version}</span>
    </UserInitialSection>
  );

  const extendedChildren = children
    ? Array.isArray(children)
      ? [...children, baseSection, versionSection]
      : [children, baseSection, versionSection]
    : [baseSection, versionSection];

  // Get section starting index
  const getNumMenuItems = (i) => {
    if (!extendedChildren[i]) return;

    let previousItems = 0;

    if (i === 0) {
      previousItems =
        extendedChildren[i].props.children.length === undefined
          ? 1
          : extendedChildren[i].props.children.length;
    }

    if (i > 0) {
      for (let x = 0; x <= i; x++) {
        previousItems =
          previousItems +
          (extendedChildren[x].props.children.length === undefined
            ? 1
            : extendedChildren[x].props.children.length);
      }
    }
    return previousItems;
  };

  const renderUserInitialsItems = (isMobile = isMobileView) => {
    const menuItems = (
      <>
        {React.Children.map(extendedChildren, (child, i) => {
          return child?.type.name === UserInitialSection.name
            ? React.cloneElement(child, {
                index: i === 0 ? i : getNumMenuItems(i - 1),
                handleClose: () => collapseMenu(true),
                innerRef: menuItemsRef,
                isMobile,
              })
            : child;
        })}
      </>
    );

    return (
      <div
        ref={containerRef}
        className={c.expanded}
        style={{ maxHeight: `${menuHeight}px` }}
      >
        {/* User info */}
        <div className={c.userInfoContainer}>
          <div className={c.avatarContainer}>
            {/* Display avatar */}
            <img src={avatarSrc} alt={avatarAlt} />
          </div>
          <div className={c.nameContainer}>
            <h1 data-sentry-mask>{name}</h1>
            {pronouns && <p className={c.pronouns}>{pronouns}</p>}
          </div>
          <div className={c.idContainer}>
            {email && (
              <div>
                <label>Email: </label>
                <span data-sentry-mask>{email}</span>
              </div>
            )}
            {id && (
              <div>
                <label>ID: </label> <span data-sentry-mask>{id}</span>
              </div>
            )}
          </div>
        </div>
        {/* User page item array */}
        {isMobileView ? (
          <>{menuItems}</>
        ) : (
          <div
            ref={menuWrapperRef}
            tabIndex={-1}
            role="menu"
            aria-label={title}
            onKeyDown={handleOnMenuKeyDown}
          >
            {menuItems}
          </div>
        )}
      </div>
    );
  };

  return isMobileView ? (
    <Modal
      triggerRef={triggerRef}
      open={shown}
      onClose={() => {
        collapseMenu(true);
      }}
      dismissOnHistoryNav={true}
      ariaLabel={title}
      ariaDescribedby={null}
      data-ignore-outside-click=""
    >
      <ModalSection
        title={title}
        titleTabIndex={null}
        ariaLabelledby="userMenuModalTitle"
        ariaDescribedby="userMenuModalContent"
        noPadding
      >
        {renderUserInitialsItems()}
      </ModalSection>
    </Modal>
  ) : (
    <Transition
      show={shown}
      enterClass={[c.enter, verticalClass].join(' ')}
      exitClass={[c.exit, verticalClass].join(' ')}
      onComponentShown={onComponentShown}
    >
      <PositionAwareMenu
        shown={mounted}
        marginTop="42px"
        horizontalAlign={align}
        offsetX={28}
        offsetY={-48}
        onReposition={onReposition}
        dismissOnHistoryNav={true}
        onDismiss={() => collapseMenu(false)}
      >
        {renderUserInitialsItems()}
      </PositionAwareMenu>
    </Transition>
  );
};

export default UserInitialsContent;
